import React, { useRef,useEffect, useState } from 'react';
import { CollaborativeCanvas } from './components/Canvas/CollaborativeCanvas';
import { CanvasComponent } from './components/Canvas/CanvasComponent';
import { Toolbar } from './components/Canvas/Toolbar';
import { 
  BackgroundType, 
  ToolType, 
  DrawingState, 
  UIState,
  Line,
  TextNote,
  LineStyle
} from './components/Canvas/types';
import { HistoryManager } from './components/UndoRedo/HistoryManager';
import './styles.css';

function App() {
  const [backgroundType, setBackgroundType] = useState<BackgroundType>('grid');
  const [isCollaborative, setIsCollaborative] = useState(false);
  const [roomId, setRoomId] = useState<string>('');

  // Обычное состояние для одиночного режима
  const [drawingState, setDrawingState] = useState<DrawingState>({ 
    lines: [],
    textNotes: []
  });
  
  const [uiState, setUiState] = useState<UIState>({
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    tool: 'pointer',
    penColor: '#000000',
    penWidth: 3,
    eraserSize: 30,
    lineStyle: 'solid',
    selection: [],
    isSelecting: false,
    isDraggingSelection: false
  });

  const historyManager = useRef(new HistoryManager(drawingState));
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Определение режима работы из URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const room = urlParams.get('room');
    const offer = urlParams.get('offer');
    
    if (room || offer) {
      setIsCollaborative(true);
      setRoomId(room || 'shared');
    } else {
      setIsCollaborative(false);
    }
  }, []);

  // Обработка истории для одиночного режима
  useEffect(() => {
    historyManager.current = new HistoryManager(drawingState);
    setCanUndo(historyManager.current.canUndo());
    setCanRedo(historyManager.current.canRedo());
  }, [drawingState]);

  // Управление состоянием для одиночного режима
  const handleDrawingStateChange = (newState: DrawingState) => {
    setDrawingState(newState);
    if (!isCollaborative) {
      historyManager.current.push(newState);
      setCanUndo(historyManager.current.canUndo());
      setCanRedo(historyManager.current.canRedo());
    }
  };

  const handleUiStateChange = (newUiState: UIState) => {
    setUiState(newUiState);
  };

  // Инструменты для одиночного режима
  const handleToolChange = (tool: ToolType) => {
    setUiState(prev => ({ ...prev, tool }));
  };

  const handleColorChange = (color: string) => {
    setUiState(prev => ({ ...prev, penColor: color }));
  };

  const handleWidthChange = (width: number) => {
    setUiState(prev => ({ ...prev, penWidth: width }));
  };

  const handleLineStyleChange = (style: LineStyle) => {
    if (uiState.selection.length > 0) {
      const newLines = drawingState.lines.map(line => 
        uiState.selection.includes(line.id) ? { ...line, lineStyle: style } : line
      );
      handleDrawingStateChange({ ...drawingState, lines: newLines });
    } else {
      setUiState(prev => ({ ...prev, lineStyle: style }));
    }
  };

  const handleLineStylesChange = (style: LineStyle) => {
    const newLines = drawingState.lines.map(line => 
      uiState.selection.includes(line.id) ? { ...line, lineStyle: style } : line
    );
    handleDrawingStateChange({ ...drawingState, lines: newLines });
  };

  const handleBackgroundChange = (type: BackgroundType) => {
    setBackgroundType(type);
  };

  const handleLineColorChange = (color: string) => {
    const newLines = drawingState.lines.map(line => 
      uiState.selection.includes(line.id) ? { ...line, color } : line
    );
    const newTextNotes = (drawingState.textNotes || []).map(note => 
      uiState.selection.includes(note.id) ? { ...note, color } : note
    );
    handleDrawingStateChange({ lines: newLines, textNotes: newTextNotes });
    setUiState(prev => ({ ...prev, penColor: color }));
  };

  const handleLineWidthChange = (width: number) => {
    const newLines = drawingState.lines.map(line => 
      uiState.selection.includes(line.id) ? { ...line, width } : line
    );
    const newTextNotes = (drawingState.textNotes || []).map(note => 
      uiState.selection.includes(note.id) ? { ...note, fontSize: width * 3 } : note
    );
    handleDrawingStateChange({ lines: newLines, textNotes: newTextNotes });
    setUiState(prev => ({ ...prev, penWidth: width }));
  };

  const handleAddLine = (line: Line) => {
    const newDrawingState = {
      ...drawingState,
      lines: [...drawingState.lines, line]
    };
    handleDrawingStateChange(newDrawingState);
  };

  const handleDeleteLine = (lineId: string) => {
    const newDrawingState = {
      ...drawingState,
      lines: drawingState.lines.filter(line => line.id !== lineId)
    };
    handleDrawingStateChange(newDrawingState);
  };

  const handleUpdateLines = (lines: Line[], textNotes?: TextNote[]) => {
    const newDrawingState = {
      lines: [...lines],
      textNotes: textNotes || drawingState.textNotes || []
    };
    handleDrawingStateChange(newDrawingState);
  };

  const handleUndo = () => {
    if (!isCollaborative) {
      const newState = historyManager.current.undo();
      if (newState) {
        setDrawingState(newState.present);
        setCanUndo(historyManager.current.canUndo());
        setCanRedo(historyManager.current.canRedo());
      }
    }
  };

  const handleRedo = () => {
    if (!isCollaborative) {
      const newState = historyManager.current.redo();
      if (newState) {
        setDrawingState(newState.present);
        setCanUndo(historyManager.current.canUndo());
        setCanRedo(historyManager.current.canRedo());
      }
    }
  };

  // Создание новой комнаты
  const createNewRoom = () => {
    const newRoomId = Math.random().toString(36).substring(2, 15);
    window.location.href = `/?room=${newRoomId}`;
  };

  // Присоединение к существующей комнате
  const joinRoom = (roomIdToJoin: string) => {
    if (roomIdToJoin.trim()) {
      window.location.href = `/?room=${roomIdToJoin.trim()}`;
    }
  };

  // Рендер одиночного режима
  const renderSingleMode = () => (
    <div className={`app ${backgroundType === 'black' ? 'dark-theme' : 'light-theme'}`}>
      <header className={`app-header ${backgroundType === 'black' ? 'dark-theme' : 'light-theme'}`}>
        <Toolbar
          currentTool={uiState.tool}
          onToolChange={handleToolChange}
          penColor={uiState.penColor}
          onColorChange={handleColorChange}
          penWidth={uiState.penWidth}
          onWidthChange={handleLineWidthChange}
          lineStyle={uiState.lineStyle}
          onLineStyleChange={handleLineStyleChange}
          onLineStylesChange={handleLineStylesChange}
          backgroundType={backgroundType}
          onBackgroundChange={handleBackgroundChange}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={handleUndo}
          onRedo={handleRedo}
          selectedLines={uiState.selection}
          onLineColorChange={handleLineColorChange}
          onLineWidthChange={handleLineWidthChange}
        />
      </header>
      
      <main className="canvas-area">
        <CanvasComponent 
          backgroundType={backgroundType}
          drawingState={drawingState}
          uiState={uiState}
          onUiStateChange={handleUiStateChange}
          onAddLine={handleAddLine}
          onDeleteLine={handleDeleteLine}
          onUpdateLines={handleUpdateLines}
        />
      </main>
    </div>
  );

  // Рендер совместного режима
  const renderCollaborativeMode = () => (
    <CollaborativeCanvas roomId={roomId} />
  );

  // Рендер выбора режима
  const renderRoomSelector = () => (
    <div className="app room-selector-page">
      <div className="room-selector-container">
        <h1>🎨 Онлайн доска для репетиторов</h1>
        
        <div className="mode-selection">
          <div className="mode-card">
            <h2>🤝 Совместное редактирование</h2>
            <p>Работайте вместе в реальном времени</p>
            <button onClick={createNewRoom} className="primary-btn">
              🆕 Создать комнату
            </button>
            
            <div className="join-room">
              <input 
                type="text" 
                placeholder="Введите ID комнаты" 
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    joinRoom(e.currentTarget.value);
                  }
                }}
              />
              <button onClick={(e) => {
                const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                joinRoom(input.value);
              }} className="secondary-btn">
                🔗 Присоединиться
              </button>
            </div>
          </div>
          
          <div className="mode-card">
            <h2>📝 Одиночный режим</h2>
            <p>Работайте самостоятельно</p>
            <button onClick={() => setIsCollaborative(false)} className="tertiary-btn">
              🖌️ Начать рисовать
            </button>
          </div>
        </div>

        <div className="features-list">
          <h3>Возможности:</h3>
          <ul>
            <li>✏️ Рисование ручкой и линиями</li>
            <li>📝 Текстовые заметки</li>
            <li>🧹 Ластик</li>
            <li>📏 Геометрические фигуры</li>
            <li>🔄 История действий</li>
            <li>🎨 Настройки цвета и стиля</li>
            <li>🖱️ Совместное редактирование через WebRTC</li>
          </ul>
        </div>
      </div>
    </div>
  );

  // Логика рендеринга
  if (isCollaborative) {
    return renderCollaborativeMode();
  } else if (!window.location.search) {
    return renderRoomSelector();
  } else {
    return renderSingleMode();
  }
}

export default App;